import {Grid} from 'src/design/atoms/Grid';
import {Stack} from 'src/design/atoms/Stack';
import React from 'react';
import {FormReferenceImage} from 'src/app/forms/FormReferenceImage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {FormDividerV2} from './FormStatic/FormDividerV2';
import {FormHeadingV2} from './FormStatic/FormHeadingV2';
import {FormParagraphV2} from './FormStatic/FormParagraphV2';
import {FormSubformV2} from './FormSubform/FormSubformV2';
import {FormViewAssetSelectorV2} from './FormViewAssetSelector/FormViewAssetSelectorV2';
import {FormViewDateV2} from './FormViewDate/FormViewDateV2';
import {FormViewDrawingPadV2} from './FormViewDrawingPad/FormViewDrawingPadV2';
import {FormViewFileListRowV2} from './FormViewFileListRow/FormViewFileListRowV2';
import {FormViewMultiSelectRowV2} from './FormViewMultiSelectRow/FormViewMultiSelectRowV2';
import {FormViewPersonSelectorV2} from './FormViewPersonSelector/FormViewPersonSelectorV2';
import {FormViewPersonnelSummaryV2} from './FormViewPersonnelSummary/FormViewPersonnelSummaryV2';
import {FormViewRichTextEditorV2} from './FormViewRichTextEditor/FormViewRichTextEditorV2';
import {FormViewRowV2} from './FormViewRow/FormViewRowV2';
import {FormViewSelectV2} from './FormViewSelect/FormViewSelectV2';
import {FormViewSignatureV2} from './FormViewSignature/FormViewSignatureV2';
import {FormViewSwitchV2} from './FormViewSwitch/FormViewSwitchV2';
import {FormViewVideoSelector} from './FormViewVideoSelector';
import {useFieldGridSpan} from './engine/useFieldGridSpan';

export interface FormViewFieldProps {
  field: IFormFieldV2;
  value: any;
}

export const FormViewField = React.memo<FormViewFieldProps>(
  ({field, value}) => {
    const span = useFieldGridSpan(field.size);

    const renderField = () => {
      switch (field.type) {
        case 'text':
        case 'textarea':
        case 'number':
        case 'phone':
        case 'scale':
          return <FormViewRowV2 field={field} value={value} />;
        case 'date':
          return <FormViewDateV2 field={field} value={value} />;
        case 'file':
          return <FormViewFileListRowV2 field={field} value={value} />;
        case 'signature':
          return <FormViewSignatureV2 field={field} value={value} />;
        case 'drawing-pad':
          return <FormViewDrawingPadV2 field={field} value={value} />;
        case 'richtext':
          return <FormViewRichTextEditorV2 field={field} value={value} />;
        case 'person':
          return <FormViewPersonSelectorV2 field={field} value={value} />;
        case 'asset':
          return <FormViewAssetSelectorV2 field={field} value={value} />;
        case 'video-select':
          return <FormViewVideoSelector field={field} value={value} />;
        case 'select':
        case 'autocomplete':
          return <FormViewSelectV2 field={field} value={value} />;
        case 'select-multiple':
          return (
            <FormViewMultiSelectRowV2
              field={field}
              value={value}
              viewLimit={10}
            />
          );
        case 'person-select-multiple':
          return <FormViewPersonnelSummaryV2 field={field} value={value} />;
        case 'switch':
          return <FormViewSwitchV2 field={field} value={value} />;
        case 'heading':
          return <FormHeadingV2 field={field} />;
        case 'paragraph':
          return <FormParagraphV2 field={field} />;
        case 'divider':
          return <FormDividerV2 field={field} />;
        case 'subform':
          return <FormSubformV2 field={field} />;
        case 'project':
          // For project fields, display the project name or 'All Projects' if not set
          return (
            <FormViewRowV2
              field={field}
              value={value?.name ?? 'All Projects'}
            />
          );
        case 'measurement': {
          const magnitude = value?.magnitude;
          const displayValue =
            magnitude === undefined || magnitude === ''
              ? ''
              : `${magnitude}${value?.unit ? ` ${value.unit}` : ''}`;
          return <FormViewRowV2 field={field} value={displayValue} />;
        }
        default:
          return null;
      }
    };

    return (
      <Grid.Col span={span}>
        <Stack gap="sm">
          {renderField()}
          <FormReferenceImage field={field} />
        </Stack>
      </Grid.Col>
    );
  }
);
